import { useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Sparkles, ScrollText, Calculator } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { renderAnswerBlocks } from "../lib/safeHtml";
import { HTTP_PAYMENT_REQUIRED } from "../lib/httpStatus";

const DEEP_KEYWORDS = ["compatibility","marriage","relationship","career","business",
  "life path","future","past life","destiny","forecast","predict"];

const estimateLocal = (q) => {
  const s = (q || "").trim().toLowerCase();
  if (!s) return 1;
  let cost = 1;
  if (s.length > 80) cost = 2;
  if (s.length > 250) cost = 3;
  if (DEEP_KEYWORDS.some(k => s.includes(k))) cost += 1;
  return Math.max(1, Math.min(cost, 4));
};

const Loader = () => (
  <div className="flex flex-col items-center py-16 gap-6">
    <svg className="glyph-loader" width="72" height="72" viewBox="0 0 72 72" fill="none">
      <circle cx="36" cy="36" r="30" stroke="#D4AF37" strokeWidth="1" strokeDasharray="4 6" />
      <circle cx="36" cy="36" r="20" stroke="#D4AF37" strokeWidth="1" opacity="0.6" />
      <path d="M36 8 L44 36 L36 64 L28 36 Z" stroke="#D4AF37" strokeWidth="1" fill="none" />
      <circle cx="36" cy="36" r="3" fill="#D4AF37" />
    </svg>
    <p className="text-sm uppercase tracking-[0.3em] text-neutral-400">Consulting the numbers…</p>
  </div>
);

const renderAnswer = (text) =>
  renderAnswerBlocks(text).map(({ key, __html }) => (
    <p key={key} dangerouslySetInnerHTML={{ __html }} />
  ));

const Ask = () => {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [question, setQuestion] = useState("");
  const [fullName, setFullName] = useState("");
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [reading, setReading] = useState(null);

  const cost = useMemo(() => estimateLocal(question), [question]);
  const canAfford = user && user.credits >= cost;
  const canSubmit = question.trim() && fullName.trim() && dob.trim();

  const ask = async () => {
    if (!fullName.trim()) { toast.error("Please enter your full name."); return; }
    if (!dob.trim()) { toast.error("Please enter your date of birth."); return; }
    if (!question.trim()) { toast.error("Please enter a question."); return; }
    if (!canAfford) {
      toast.error("Not enough credits — please buy more.");
      nav("/pricing");
      return;
    }
    setLoading(true);
    setReading(null);
    try {
      const { data } = await api.post("/numerology/ask", {
        question,
        full_name: fullName,
        dob,
      });
      setReading(data.reading);
      await refresh();
      toast.success(`Reading revealed · ${data.reading.cost} credit${data.reading.cost > 1 ? "s" : ""} used`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong.";
      toast.error(msg);
      if (e?.response?.status === HTTP_PAYMENT_REQUIRED) nav("/pricing");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative mx-auto max-w-4xl px-4 sm:px-6 py-8 sm:py-12">
      <div className="mb-8 sm:mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3">Reading</p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tighter">Ask your question</h1>
        <p className="text-neutral-400 mt-3 max-w-xl text-sm sm:text-base">
          Share your full name and birth date, then ask anything. Cost is calculated by depth — 1 credit for a lookup, up to 4 for a full interpretation.
        </p>
      </div>

      <div className="rounded-2xl border border-white/5 bg-[#141419] p-5 sm:p-8">
        <div className="grid gap-5 md:grid-cols-2 mb-6">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
              Full name <span className="text-gold">*</span>
            </Label>
            <Input
              data-testid="fullname-input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Amara Blackwood"
              required
              className="mt-3 bg-black/40 border-white/10 focus-visible:ring-gold"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
              Date of birth <span className="text-gold">*</span>
            </Label>
            <Input
              data-testid="dob-input"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              required
              max={new Date().toISOString().slice(0, 10)}
              className="mt-3 bg-black/40 border-white/10 focus-visible:ring-gold text-neutral-200"
            />
          </div>
        </div>

        <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
          Your question <span className="text-gold">*</span>
        </Label>
        <Textarea
          data-testid="question-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Will this year bring the change I'm waiting for?"
          required
          className="mt-3 min-h-[140px] bg-black/40 border-white/10 focus-visible:ring-gold text-base"
        />

        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div
            data-testid="cost-badge"
            className="flex items-center gap-3 rounded-full border border-gold/30 bg-gold/5 px-4 py-2 self-start"
          >
            <Calculator className="h-4 w-4 text-gold" />
            <span className="text-sm text-neutral-300">
              This reading will cost <span className="text-gold font-medium">{cost}</span> credit{cost > 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-3">
            <span className="text-sm text-neutral-400">
              Balance: <span className="text-white">{user?.credits ?? 0}</span>
            </span>
            <Button
              onClick={ask}
              disabled={loading || !canSubmit}
              data-testid="ask-btn"
              className="rounded-full bg-gold text-black hover:bg-[#F3E5AB] px-6 sm:px-8 transition-colors duration-200 disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {loading ? "Consulting…" : "Reveal"}
            </Button>
          </div>
        </div>
      </div>

      {loading && <Loader />}

      {reading && (
        <div className="mt-8 sm:mt-10 fade-up">
          <div className="reading-parchment rounded-2xl border border-gold/20 bg-[#141419] p-6 sm:p-10 shadow-gold">
            <div className="relative">
              <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">The Reading</p>
              <h2 className="font-serif text-xl sm:text-2xl lg:text-3xl tracking-tight mb-5 sm:mb-6 text-white break-words">
                {reading.question}
              </h2>
              <div className="reading-content" data-testid="reading-answer">
                {renderAnswer(reading.answer)}
              </div>
              <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between text-xs text-neutral-500">
                <span>Cost · {reading.cost} credit{reading.cost > 1 ? "s" : ""}</span>
                <Link to="/history" className="hover:text-gold flex items-center gap-1.5 transition-colors duration-200">
                  <ScrollText className="h-3.5 w-3.5" /> All readings
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ask;
